// Workspace → SMS number opt-in-proof uploader. On file select it runs the
// presigned three-step flow, then stores the verified key in the hidden
// opt_in_proof_key field the request form submits:
//   1. POST /workspace/{id}/sms-number/opt-in-proof/sign   → presigned PUT URL
//   2. PUT bytes directly to the presigned URL
//   3. POST /workspace/{id}/sms-number/opt-in-proof/commit → returns the key
// The three-step flow mirrors profile_avatar.js.

import { onceGuard } from "./bind_once.js";

const firstBind = onceGuard();

const ROOT_SELECTOR = "[data-sms-optin-upload]";
const FALLBACK_ERROR = "Could not upload the proof. Please try again.";

export function initSMSOptInProofUpload(root = document) {
  root.querySelectorAll(ROOT_SELECTOR).forEach(wireWidget);
}

function wireWidget(widget) {
  if (!firstBind(widget)) return;

  const input = widget.querySelector("[data-sms-optin-input]");
  const keyEl = widget.querySelector("[data-sms-optin-key]");
  const statusEl = widget.querySelector("[data-sms-optin-status]");
  const errorEl = widget.querySelector("[data-sms-optin-error]");
  const workspaceID = widget.dataset.workspaceId;
  if (!input || !keyEl || !workspaceID) return;

  const base = `/workspace/${workspaceID}/sms-number/opt-in-proof`;

  input.addEventListener("change", async () => {
    const file = input.files?.[0];
    if (!file) return;

    hide(errorEl);
    setStatus(statusEl, "Uploading…");
    input.disabled = true;

    try {
      const key = await uploadOne(file, base);
      keyEl.value = key;
      setStatus(statusEl, "Proof uploaded.");
    } catch (err) {
      keyEl.value = "";
      hide(statusEl);
      showError(errorEl, err?.message || FALLBACK_ERROR);
    } finally {
      input.disabled = false;
      input.value = "";
    }
  });
}

async function uploadOne(file, base) {
  const signParams = new URLSearchParams();
  signParams.append("filename", file.name);
  signParams.append("content_type", file.type || "application/octet-stream");
  signParams.append("file_size_bytes", String(file.size));

  const signResp = await fetch(`${base}/sign`, {
    method: "POST",
    body: signParams,
    headers: { Accept: "application/json" },
    credentials: "same-origin",
  });
  if (!signResp.ok) throw await asUserError(signResp);

  const signed = await signResp.json();
  if (!signed.upload_url || !signed.key) throw new Error(FALLBACK_ERROR);

  // upload_headers are the signed metadata headers; the store rejects the
  // PUT signature unless they're echoed back verbatim.
  const putResp = await fetch(signed.upload_url, {
    method: "PUT",
    body: file,
    headers: {
      "Content-Type": file.type || "application/octet-stream",
      ...(signed.upload_headers || {}),
    },
  });
  if (!putResp.ok) throw new Error(FALLBACK_ERROR);

  const commitParams = new URLSearchParams();
  commitParams.append("key", signed.key);

  const commitResp = await fetch(`${base}/commit`, {
    method: "POST",
    body: commitParams,
    headers: { Accept: "application/json" },
    credentials: "same-origin",
  });
  if (!commitResp.ok) throw await asUserError(commitResp);

  const committed = await commitResp.json();
  if (!committed.key) throw new Error(FALLBACK_ERROR);

  return committed.key;
}

async function asUserError(resp) {
  try {
    const body = await resp.json();
    if (body && typeof body.error === "string") return new Error(body.error);
  } catch (_) {
    // not JSON — fall through
  }
  return new Error(`Request failed (${resp.status})`);
}

function showError(el, message) {
  if (!el) return;
  el.textContent = message;
  el.classList.remove("hidden");
}

function hide(el) {
  if (!el) return;
  el.classList.add("hidden");
}

function setStatus(el, message) {
  if (!el) return;
  el.textContent = message;
  el.classList.remove("hidden");
}
